import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { CountryFlag } from "@ui/components/common/country-flag";
import { GET_COUNTRY_NAME } from "@ui/components/inputs/country-input";
import { countries } from "countries-list";
import { countries as countryCodes } from "country-flag-icons";
import { CountryCode, isSupportedCountry } from "libphonenumber-js";
import { Dropdown } from "primereact/dropdown";
import React, { useMemo } from "react";

export const DEFAULT_COUNTRY_CODE = "FR";

interface PhoneCountryDropdownProps {
	value: string;
	onChange: (value: string) => void;
}

export const PhoneCountryDropdown = ({ value, onChange }: PhoneCountryDropdownProps): JSX.Element => {
	const { format } = useI18n();

	// Generate country list. This step also makes sure that each country code is mutually recognized by
	//  - "country-flag-icons" which provides the flag icons
	//  - "countries-list" which provides the localized country name
	//  - "libphonenumber-js" which provides the phone number validation
	const availableCountryCodes = useMemo<CountryCode[]>(
		() => countryCodes.filter(code => code in countries).filter(code => isSupportedCountry(code)),
		[],
	);

	return (
		<StyledDropdown
			value={value}
			onChange={e => onChange((e.value as CountryCode | undefined) ?? DEFAULT_COUNTRY_CODE)}
			options={availableCountryCodes}
			filter
			showFilterClear
			resetFilterOnHide
			filterPlaceholder={format("global.form.phoneNumberInput.country.search.placeholder")}
			emptyFilterMessage={format("global.form.phoneNumberInput.country.search.empty")}
			valueTemplate={selectedCountryTemplate}
			itemTemplate={countryOptionTemplate}
		/>
	);
};

const selectedCountryTemplate = (code: string | undefined) => <StyledCountryFlag code={code} />;

const countryOptionTemplate = (code: string) => (
	<DropdownItemContainer>
		<StyledCountryFlag code={code} />
		<div>{GET_COUNTRY_NAME(code)}</div>
	</DropdownItemContainer>
);

const DropdownItemContainer = styled.div`
	display: flex;
	gap: 0.75em;
`;

const StyledCountryFlag = styled(CountryFlag)`
	height: 1em;
	width: 1.5em;
	border-radius: 2px;
`;

const StyledDropdown = styled(Dropdown)`
	width: unset;
	background-color: unset;
	border-radius: unset;
	border: unset;
	padding: 1em;
	padding-left: 1.5em;
	gap: 0.75em;

	.p-dropdown-label {
		border-radius: unset;
		padding: 0;
		display: grid;
		place-content: center;
	}

	.p-dropdown-trigger {
		border-radius: unset;
		padding: 0;
		width: unset;
	}
`;
